import React, { useEffect, useState } from 'react';
import { Squad } from '../../../types';
import { getCurriculumAdapterById } from '../../curriculum/adapters/registry';
import { listManagedCurricula, subscribeManagedCurriculum } from '../../curriculum/services/curriculumService';
import { LevelLite } from '../../curriculum/adapters/types';
import { resolveSessionPlan } from '../utils/sessionPlan';
import { changeCurriculumFromSession } from '../utils/curriculumAssignment';
import { useAssignedCurricula } from '../utils/useAssignedCurricula';

interface Props {
  squad: Squad;
  options: Array<{ id: string; label: string }>;
  onChange: (plan: NonNullable<Squad['sessionPlan']>) => void;
}

export default function CurriculumScheduleEditor({ squad, options, onChange }: Props) {
  const baseId = squad.curriculumId || 'music';
  const [baseLevels, setBaseLevels] = useState<LevelLite[]>(getCurriculumAdapterById(baseId).levels);
  const [start, setStart] = useState('');
  const [curriculumId, setCurriculumId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setBaseLevels(getCurriculumAdapterById(baseId).levels);
    return subscribeManagedCurriculum(baseId, curriculum => setBaseLevels(curriculum?.published !== false && curriculum?.levels?.length ? curriculum.levels : getCurriculumAdapterById(baseId).levels), () => setError('Could not load curriculum. Please try again.'));
  }, [baseId]);
  const levels = useAssignedCurricula(squad, baseId, baseLevels);
  const plan = resolveSessionPlan(squad, levels, baseId);
  const lastLocked = plan.slots.reduce((last, slot) => slot.status === 'completed' || slot.status === 'live' ? Math.max(last, slot.index) : last, 0);
  const available = plan.slots.filter(slot => slot.index > lastLocked);
  const selectedStart = start || String(available[0]?.index || '');
  const groups = plan.slots.reduce<Array<{ start: number; end: number; id: string }>>((result, slot) => {
    const id = slot.curriculumId || baseId;
    const previous = result[result.length - 1];
    if (previous?.id === id) previous.end = slot.index;
    else result.push({ start: slot.index, end: slot.index, id });
    return result;
  }, []);
  return <div className="squad-details-curriculum-plan">
    <strong>Curriculum by session</strong>
    <p>Change the curriculum for the remaining sessions, starting at lesson 1. Completed and live classes stay unchanged.</p>
    {groups.map(group => <div key={group.start}>Sessions {group.start}–{group.end}: <strong>{options.find(option => option.id === group.id)?.label || group.id}</strong></div>)}
    {plan.mismatch && <p>{plan.mismatch}</p>}
    <div className="squad-details-curriculum-controls">
      <label>Starting session<select className="squad-details-inline-input" value={selectedStart} disabled={busy || !available.length} onChange={event => setStart(event.target.value)}>{available.map(slot => <option key={slot.index} value={slot.index}>Session {slot.index}{slot.date ? ` · ${slot.date}` : ''}</option>)}</select></label>
      <label>New curriculum<select className="squad-details-inline-input" value={curriculumId} disabled={busy || !available.length} onChange={event => setCurriculumId(event.target.value)}><option value="">Select curriculum…</option>{options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>
      <button type="button" disabled={busy || !curriculumId || !available.length} onClick={async () => {
        setBusy(true); setError('');
        try {
          const managed = (await listManagedCurricula()).find(item => item.id === curriculumId);
          if (managed?.published === false) throw new Error('Publish this curriculum before assigning it.');
          const nextLevels = managed?.levels?.length ? managed.levels : getCurriculumAdapterById(curriculumId).levels;
          onChange(changeCurriculumFromSession(squad, plan, Number(selectedStart), curriculumId, nextLevels));
        } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not change curriculum.'); }
        finally { setBusy(false); }
      }}>{busy ? 'Updating…' : 'Apply to remaining sessions'}</button>
    </div>
    {!available.length && <p>All sessions are completed or live.</p>}
    <small>Save changes below to save this plan.</small>
    {error && <p role="alert">{error}</p>}
  </div>;
}
